import { isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Polling from '../../../../common/services/Polling';
import TelemetryAggregationService from '../../../../common/services/telemetry/TelemetryAggregationService';
import Currently from './components/Currently';

import FiltersToolbar from './components/Filters';
import { defaultFilterValues, demoFilterValues } from './components/Filters/filterValues';
import Graph from './components/Graph';
import MessageComponent from './components/Messages';
import { useFilters } from './hooks/useFilters';

import { useRealTimeGraphUpdate } from './hooks/useRealTimeGraphUpdate';
import { LeftColumn, MainColumn, MainWrapper, MetricGraphsContainer, TelemetryTabWrapper, TitleWrapper } from './style';
import ConsolidatedChargerEchart from '../ConsolidatedChargerEchart';

const metricPolling = new Polling();

function TelemetryComponent(props) {
  const {
    chargerId,
    loadMetricsDictionary,
    loadRealTimeMetrics,
    loadHistoryMetrics,
    metricDictionary,
    chargerMetrics,
    clearMetrics,
    demo: isDemo,
    realTime,
    defaultDotsLimit,
    telemetryPeriodicity,
  } = props;
  const { dataType } = useParams();
  const isRealTime = isUndefined(dataType) ? realTime : dataType === 'realtime';
  const formOptions = !isDemo ? defaultFilterValues : demoFilterValues;
  const showMetricsFilter = metricDictionary.length > 0 || isDemo;

  const [isConsolidatedView, setIsConsolidatedView] = useState(true);

  const TAS = new TelemetryAggregationService({
    defaultLimit: defaultDotsLimit,
    aggregationInterval: telemetryPeriodicity,
    usePhases: false, // will add 3 to count since we are aggregating by min, max, avg
  });

  useEffect(() => loadMetricsDictionary(), []);

  const { filters, values, selectedMetrics, resetFilters, onChange, onSubmit, setValues } = useFilters({
    formOptions,
    metricDictionary,
  });

  const { startPolling } = useRealTimeGraphUpdate({
    metricPolling,
    values,
    filters,
    setValues,
    loadMetrics: loadRealTimeMetrics,
    isDemo,
  });

  const setCheckBoxValue = () => setIsConsolidatedView((prevState) => !prevState);

  useEffect(() => {
    clearMetrics();
    metricPolling.stop(); // avoid re-render history graph
    resetFilters();
  }, [isRealTime]);

  const title = isRealTime ? 'Real Time Data' : 'Historical Data';

  useEffect(() => {
    if (selectedMetrics.length > 0) {
      if (isRealTime) {
        startPolling();
      } else {
        const tasFilters = { ...filters, TAS };
        loadHistoryMetrics(tasFilters, isDemo);
      }
    }

    return () => metricPolling.stop();
  }, [selectedMetrics, values.utc]);

  return (
    <TelemetryTabWrapper id="chargerTelemetry">
      <TitleWrapper>
        <h3>{title}</h3>
      </TitleWrapper>

      <FiltersToolbar
        isRealTime={isRealTime}
        showMetrics={showMetricsFilter}
        demo={isDemo}
        filters={filters}
        onChange={onChange}
        onSubmit={onSubmit}
        isConsolidatedView={isConsolidatedView}
        setIsConsolidatedView={setCheckBoxValue}
      />

      <MainWrapper>
        {isRealTime && (
          <LeftColumn>
            <Currently chargerId={chargerId} demo={isDemo} />
          </LeftColumn>
        )}

        <MainColumn className="charger__telemetry_content">
          <MessageComponent metrics={metricDictionary} selectedMetrics={selectedMetrics} demo={isDemo} />
          {isConsolidatedView ? (
            <MetricGraphsContainer>
              <ConsolidatedChargerEchart
                selectedMetrics={selectedMetrics}
                chargerMetrics={chargerMetrics}
                start={values.from}
                end={values.to}
                offset={values.utc}
                isRealTime={isRealTime}
                isDemo={isDemo}
              />
            </MetricGraphsContainer>
          ) : (
            selectedMetrics.length > 0 && (
              <MetricGraphsContainer>
                {selectedMetrics.map((metric) => (
                  <Graph
                    key={metric.code}
                    metric={metric}
                    chargerMetrics={chargerMetrics}
                    values={values}
                    start={values.from}
                    end={values.to}
                    offset={values.utc}
                    isRealTime={isRealTime}
                    demo={isDemo}
                  />
                ))}
              </MetricGraphsContainer>
            )
          )}
        </MainColumn>
      </MainWrapper>
    </TelemetryTabWrapper>
  );
}

export default TelemetryComponent;
